import React, { ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button, { ButtonTheme } from 'components/Form/Button'
import { Routes } from 'routes'
import { FormType } from 'types/various'
import ModalAction from 'components/Modal/ModalAction'
import { AppContext } from 'contexts/AppContext'
import { CAMPAIGN_ACTION } from 'constant/authorization'
import './VoohCampaignActionButtons.scss'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { NotifierType } from '../../../../components/Notifier'
import { RootState } from '../../../../store'
import { CampaignPermissionType, FeatureName } from 'types/features'
import { VoohCampaignActionButtonsProps } from './types'
import useVoohCampaignAction from '../../../../hooks/useVoohCampaignAction'
import Modal from '../../../../components/Modal'
import IconAlert from '../../../../components/Icon/IconAlert'
import { AlertType, VoohCampaignPotential } from '../../../../types/campaign'
import FillingSpinner from '../../../../components/FillingSpinner'
import { FillingSpinnerColor } from '../../../../components/FillingSpinner/types'
import Table, {
  TableBody,
  TableData,
  TableHeader,
  TableHeaderItem,
  TableRow,
} from '../../../../components/Table'

const VoohCampaignActionButtons: React.FC<VoohCampaignActionButtonsProps> = ({ formType }) => {
  const { t } = useTranslation()
  const { allowedFor } = useContext(AppContext)
  const voohCampaign = useSelector((state: RootState) => state.voohCampaignForm)
  const { id: campaignId } = voohCampaign.form
  const { name, clubMediaSource } = voohCampaign.form.baseValues
  const { acceptVoohCampaign, checkVoohCampaignPotential } = useVoohCampaignAction()
  const [isApproveModalOpen, setIsApproveModalOpen] = useState<boolean>(false)
  const [showPotentialCheckLoader, setShowPotentialCheckLoader] = useState<boolean>(false)

  const [isPotentialExceededOpen, setIsPotentialExceededOpen] = useState<
    'activate' | 'publish' | null
  >(null)
  const [approveCampaignLoading, setApproveCampaignLoading] = useState<boolean>(false)
  const [isPublishModalOpen, setIsPublishModalOpen] = useState<boolean>(false)
  const [publishCampaignLoading, setPublishCampaignLoading] = useState<boolean>(false)
  const [exceedingPotential, setExceedingPotential] = useState<VoohCampaignPotential[]>([])

  const voohForm = useSelector((state: RootState) => state.voohCampaignForm)
  const status = voohForm.status

  const canEdit = allowedFor({
    template: CAMPAIGN_ACTION.EDIT,
    feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Write },
    status,
  })

  const canApprove = allowedFor({
    template: CAMPAIGN_ACTION.APPROVE,
    status,
  })

  const canPublish = allowedFor({
    template: CAMPAIGN_ACTION.PUBLISH,
    feature: { name: FeatureName.Campaigns, action: CampaignPermissionType.Publish },
    status,
  })

  const showContainer = canEdit || canApprove || canPublish
  const navigate = useNavigate()
  const { addNotification } = useContext(AppContext)

  const saveCampaign = () => {
    navigate(Routes.VOOH_CAMPAIGNS.ONE(campaignId))
    addNotification(NotifierType.SUCCESS, t('form.campaign.save.success'))
  }

  const handleActivateOrPublish = async (action: 'activate' | 'publish') => {
    setShowPotentialCheckLoader(true)
    const potential = await checkVoohCampaignPotential(campaignId, clubMediaSource?.rawData)
    setExceedingPotential(potential)
    setShowPotentialCheckLoader(false)
    if (potential.length) {
      setIsPotentialExceededOpen(action)
    } else {
      if (action === 'activate') {
        setIsApproveModalOpen(true)
      } else {
        setIsPublishModalOpen(true)
      }
    }
  }

  const ExceededMaximumPotentialWarnMessage = (
    action: 'activate' | 'publish' | null
  ): ReactNode => (
    <>
      <div className={'Alerts__header'}>
        <IconAlert alertType={AlertType.alert} />
        <h1>{t('common.alert')}</h1>
      </div>
      <h3>
        {t(
          `form.campaign.${
            action === 'activate'
              ? 'exceedingMaximumPotentialTextForActivatingCampaign'
              : 'exceedingMaximumPotentialTextForPublishedCampaign'
          }`
        )}
      </h3>
      <div className='CampaignActions__exceeding-potential-table-container'>
        <Table loading={false}>
          <TableHeader sticky>
            <TableHeaderItem>{t('common.brand')}</TableHeaderItem>
            <TableHeaderItem>{t('common.network')}</TableHeaderItem>
            <TableHeaderItem>{t('common.month')}</TableHeaderItem>
            <TableHeaderItem>{t('common.exceeding')}</TableHeaderItem>
          </TableHeader>

          <TableBody>
            {exceedingPotential.map((potential: VoohCampaignPotential, index) => (
              <TableRow
                key={index}
                className={'CampaignActions__exceeded-potential-table--row'}
              >
                <TableData>{potential.brand}</TableData>
                <TableData>{potential.network}</TableData>
                <TableData>{potential.month}</TableData>
                <TableData>
                  {clubMediaSource?.rawData
                    ? potential.takenClubMinutes - potential.totalClubMinutes
                    : potential.takenMoveMinutes - potential.totalMoveMinutes + ' min'}
                </TableData>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  )

  const Container = (): ReactNode => (
    <div className='CampaignActions'>
      {canEdit &&
        (formType === FormType.NEW ? (
          <Button
            theme={ButtonTheme.BLUE_OUTLINE}
            toUrl={Routes.VOOH_CAMPAIGNS.ONE(campaignId)}
          >
            {t('form.campaign.approval.saveAsDraft')}
          </Button>
        ) : (
          <Button
            theme={ButtonTheme.BLUE_OUTLINE}
            onClick={() => void saveCampaign()}
            dataCy={'save-campaign-btn'}
          >
            {t('common.save')}
          </Button>
        ))}

      {canApprove && (
        <Button
          dataCy={'activate-campaign-btn'}
          onClick={() => handleActivateOrPublish('activate')}
        >
          <div
            className={
              showPotentialCheckLoader ? 'CampaignActions__buttons-container' : 'CampaignActions'
            }
          >
            <span>{t('common.activateCampaign')}</span>
            {showPotentialCheckLoader && (
              <FillingSpinner
                color={FillingSpinnerColor.WHITE}
                className='CampaignActions__potential-check-spinner'
              />
            )}
          </div>
        </Button>
      )}

      {canPublish && (
        <Button
          onClick={() => handleActivateOrPublish('publish')}
          disabled={!voohCampaign.publishable}
        >
          <div
            className={
              showPotentialCheckLoader ? 'CampaignActions__buttons-container' : 'CampaignActions'
            }
          >
            <span>{t('common.publishChanges')}</span>
            {showPotentialCheckLoader && (
              <FillingSpinner
                color={FillingSpinnerColor.WHITE}
                className='CampaignActions__potential-check-spinner'
              />
            )}
          </div>
        </Button>
      )}

      {isApproveModalOpen && (
        <ModalAction
          title={t('modal.approveCampaign')}
          itemText={t('modal.doYouWantToApproveCampaign')}
          itemName={name}
          actionText={t('modal.activate')}
          isOpen={isApproveModalOpen}
          setIsOpen={setIsApproveModalOpen}
          isLoading={approveCampaignLoading}
          setIsLoading={setApproveCampaignLoading}
          onAction={() => acceptVoohCampaign(campaignId, 'activate')}
        />
      )}

      {isPublishModalOpen && (
        <ModalAction
          title={t('modal.publishingChanges')}
          itemText={t('modal.doYouWantToPublishCampaign')}
          itemName={name}
          actionText={t('modal.publish')}
          isOpen={isPublishModalOpen}
          setIsOpen={setIsPublishModalOpen}
          isLoading={publishCampaignLoading}
          setIsLoading={setPublishCampaignLoading}
          onAction={() => acceptVoohCampaign(campaignId, 'publish')}
        />
      )}

      {isPotentialExceededOpen && (
        <Modal
          classNameChildren='AlertsCard__modal--content'
          isOpen={!!isPotentialExceededOpen}
          onClose={() => void setIsPotentialExceededOpen(null)}
          separateTitleBar={false}
          footer={
            <div className={'CampaignActions__exceeding-potential-modal__footer'}>
              <Button
                theme={ButtonTheme.BLUE}
                onClick={() => {
                  setPublishCampaignLoading(true)
                  acceptVoohCampaign(campaignId, isPotentialExceededOpen).then(() => {
                    setPublishCampaignLoading(false)
                    void setIsPotentialExceededOpen(null)
                  })
                }}
              >
                <div
                  className={
                    publishCampaignLoading
                      ? 'CampaignActions__buttons-container'
                      : 'CampaignActions'
                  }
                >
                  <span>{t(`modal.${isPotentialExceededOpen}`)}</span>
                  {publishCampaignLoading && (
                    <FillingSpinner
                      color={FillingSpinnerColor.WHITE}
                      className='CampaignActions__potential-check-spinner'
                    />
                  )}
                </div>
              </Button>
              <Button
                theme={ButtonTheme.BLUE_OUTLINE}
                onClick={() => void setIsPotentialExceededOpen(null)}
              >
                {t('common.cancel')}
              </Button>
            </div>
          }
          scrollable
        >
          <div className='AlertsCard__modal'>
            <>{ExceededMaximumPotentialWarnMessage(isPotentialExceededOpen)}</>
          </div>
        </Modal>
      )}
    </div>
  )

  return <>{showContainer && Container()}</>
}

export default VoohCampaignActionButtons
